import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CaretDown } from '@phosphor-icons/react';
import { formatDateOnly } from '@/lib/utils/custom-date/custom-date';
import { workDaySettingsOf } from '@/lib/utils/work-days/work-days';
import { InlineSkeleton } from '@/components/core';
import { Badge } from '@/components/ui-kit/badge';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui-kit/collapsible';
import { cn } from '@/lib/utils';
import { useGetEmployeeFineTotals } from '../../hooks/use-profile-view-lookups';
import {
  useEmployeeWorkStatusEligibility,
  useIsWorkStatusEligibilityFetching,
} from '../../hooks/use-work-status-eligibility';
import { isWorkingFromHome, sortOfficeDays } from '../../schemas/work-status';
import { Employee, HrTermRecord } from '../../types/employee.types';
import { InfoCard, InfoRow } from './info-row';

interface TermDetailsProps {
  employee: Employee;
  term: HrTermRecord;
  /** Work from home / lunch / overtime describe the person now, so they only belong on the active term. */
  isCurrent: boolean;
}

/**
 * Everything one HR term holds: its period, working info, leave allotment, carry-forwards and its fines.
 */
const TermDetails = ({ employee, term, isCurrent }: TermDetailsProps) => {
  const { t } = useTranslation();
  // Lunch / overtime are on when the employee holds PROFILE.LUNCH_ENABLED / OVERTIME_ENABLED;
  // left blank while that isn't known.
  const eligibility = useEmployeeWorkStatusEligibility(isCurrent ? employee.UserId : undefined);
  // A placeholder while the IAM look-up runs; a dash when IAM can't be read (as before).
  const isEligibilityFetching = useIsWorkStatusEligibilityFetching(
    isCurrent ? employee.UserId : undefined
  );
  const statusText = (isOn: boolean | undefined) =>
    isOn === undefined ? (
      isEligibilityFetching ? (
        <InlineSkeleton className="w-16" />
      ) : undefined
    ) : isOn ? (
      t('ACTIVE')
    ) : (
      t('INACTIVE')
    );
  const isWfh = isWorkingFromHome(employee);
  const { data: fines, isLoading: isFinesLoading } = useGetEmployeeFineTotals(
    employee.UserId,
    term.HrPeriodStartDate,
    term.HrPeriodEndDate
  );

  // One pair of rows per work-day setting; a term whose work days change shows each from its first day.
  const workDaySettings = workDaySettingsOf(term.WorkDayHours);
  const leaves = term.LeaveDetails.filter((leave) => leave.IsApplicable);

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <InfoCard title={t('HR_PERIOD')}>
          <InfoRow label={t('EMPLOYMENT_TYPE')} value={term.EmploymentType} />
          <InfoRow label={t('HR_PERIOD_START')} value={formatDateOnly(term.HrPeriodStartDate)} />
          <InfoRow label={t('HR_PERIOD_END')} value={formatDateOnly(term.HrPeriodEndDate)} />
          <InfoRow label={t('FLEXIBLE')} value={term.IsFlexible ? t('YES') : t('NO')} />
        </InfoCard>

        <InfoCard title={t('WORKING_INFO')}>
          {workDaySettings.length === 0 && <InfoRow label={t('WORKING_DAYS')} />}
          {workDaySettings.map(({ effectiveFrom, days }) => {
            const totalMinutes = days.reduce(
              (sum, day) => sum + Math.round(day.WorkingHours * 60),
              0
            );
            const suffix =
              workDaySettings.length > 1
                ? ` (${effectiveFrom ? t('WORK_DAYS_FROM_DATE', { date: formatDateOnly(days[0].EffectiveFrom) }) : t('WORK_DAYS_FROM_TERM_START')})`
                : '';
            return (
              <Fragment key={effectiveFrom || 'start'}>
                <InfoRow
                  label={`${t('WORKING_DAYS')}${suffix}`}
                  value={days.map((entry) => t(entry.WorkDay)).join(', ')}
                />
                <InfoRow
                  label={t('TOTAL_WORKING_HOURS')}
                  value={`${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`}
                />
              </Fragment>
            );
          })}
          {isCurrent && (
            <>
              <InfoRow label={t('WFH')} value={isWfh ? t('ACTIVE') : t('INACTIVE')} />
              {!isWfh && (
                <InfoRow
                  label={t('WORK_FROM_OFFICE')}
                  value={sortOfficeDays(employee.WFODays)
                    .map((day) => t(day))
                    .join(', ')}
                />
              )}
              <InfoRow label={t('LUNCH_STATUS')} value={statusText(eligibility?.lunch)} />
              <InfoRow label={t('OVERTIME')} value={statusText(eligibility?.overtime)} />
            </>
          )}
        </InfoCard>

        <InfoCard title={t('LEAVE_ALLOTMENT')}>
          {leaves.length === 0 && <InfoRow label={t('LEAVE_ALLOTMENT')} />}
          {leaves.map((leave) => (
            <InfoRow
              key={leave.LeaveCategoryId}
              label={
                leave.IsDefault
                  ? `${leave.LeaveCategory} (${t('DEFAULT_LEAVE')})`
                  : leave.LeaveCategory
              }
              value={
                leave.IsUnlimited ? t('UNLIMITED') : `${leave.LeaveLimitTotalDays} ${t('DAYS')}`
              }
            />
          ))}
          <InfoRow
            label={t('LEAVE_CARRY_FORWARD')}
            value={`${term.LeaveCarryForwardDays} ${t('DAYS')}`}
          />
          <InfoRow
            label={t('TOIL_CARRY_FORWARD')}
            value={`${term.ToilCarryForwardDays} ${t('DAYS')}`}
          />
          <InfoRow
            label={t('SPECIAL_TOIL_CARRY_FORWARD')}
            value={`${term.SpecialToilCarryForwardDays} ${t('DAYS')}`}
          />
        </InfoCard>

        <InfoCard title={t('FINE_INFORMATION')}>
          {/* Not "0" while the totals load: it would read as "no fines". */}
          <InfoRow
            label={t('LUNCH_FINE')}
            value={isFinesLoading ? <InlineSkeleton className="w-12" /> : `${fines?.lunch ?? 0}`}
          />
          <InfoRow
            label={t('NON_COMPLIANCE_FINE')}
            value={
              isFinesLoading ? <InlineSkeleton className="w-12" /> : `${fines?.nonCompliance ?? 0}`
            }
          />
        </InfoCard>
      </div>
    </div>
  );
};

interface TermBoxProps {
  employee: Employee;
  term: HrTermRecord;
  defaultOpen: boolean;
}

const TermBox = ({ employee, term, defaultOpen }: TermBoxProps) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="rounded-lg border">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className={cn(
            'flex w-full flex-wrap items-center gap-2 bg-muted/40 px-4 py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600',
            isOpen ? 'rounded-t-lg border-b' : 'rounded-lg'
          )}
        >
          <CaretDown
            className={cn('size-4 shrink-0 transition-transform', !isOpen && '-rotate-90')}
          />
          <span className="text-sm font-semibold">
            {t('HR_TERM')} {term.HRPeriod}
          </span>
          <span className="text-xs text-muted-foreground">
            {formatDateOnly(term.HrPeriodStartDate)} - {formatDateOnly(term.HrPeriodEndDate)}
            {term.EmploymentType ? ` · ${term.EmploymentType}` : ''}
          </span>
          {term.IsCurrentHrYear && (
            <Badge className="ml-auto" variant="default">
              {t('ACTIVATED')}
            </Badge>
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="p-4">
        <TermDetails employee={employee} term={term} isCurrent={term.IsCurrentHrYear} />
      </CollapsibleContent>
    </Collapsible>
  );
};

/**
 * "HR Info": every HR term (`UserHRPeriodicalProfile`) of the person as an expandable box, newest first,
 * with the active term open (the latest one while none is active).
 */
export const HrInfoTab = ({ employee }: { employee: Employee }) => {
  const { t } = useTranslation();
  const terms = [...employee.HrTerms].reverse();
  const openTermId = (terms.find((term) => term.IsCurrentHrYear) ?? terms[0])?.ItemId;

  if (terms.length === 0) {
    return <p className="text-sm text-muted-foreground">{t('NO_DATA_FOUND')}</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {terms.map((term) => (
        <TermBox
          key={term.ItemId}
          employee={employee}
          term={term}
          defaultOpen={term.ItemId === openTermId}
        />
      ))}
    </div>
  );
};
